import { MouseEvent as ReactMouseEvent, FC, useEffect } from 'react';
import * as Styles from './login-carousel.module.less';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import Slider, { Settings, LazyLoadTypes, CustomArrowProps } from 'react-slick';
import {
    Button,
    Link,
    Icon,
    Headline,
    BodyText,
    Stack,
    Spinner,
} from '@servicetitan/design-system';
import { tokens as Tokens } from '@servicetitan/tokens/core';
import { Transition } from 'semantic-ui-react';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { LoginStore } from '../../stores/login.store';
import { LOGIN_CAROUSEL_CODE_ID, marketingCarouselFallbackData } from 'components/lib/contentful';
import { useQueryParams } from '@servicetitan/react-hooks';
import {
    MarketingCarouselStore,
    MarketingCarouselColorTheme as Theme,
} from '@servicetitan/onboarding-ui/contentful';
import { ContentfulApi, CONTENTFUL_API_TOKEN } from '@servicetitan/contentful-proxy';

interface CarouselNavProps extends CustomArrowProps {
    direction: 'Next' | 'Previous';
}

interface Props {
    visible: boolean;
}

const CarouselNavArrow = observer((props: CarouselNavProps) => {
    const clickHandler = (e: ReactMouseEvent<any, MouseEvent>) => props.onClick!(e);
    const [{ carouselSettings }] = useDependencies(MarketingCarouselStore);
    const colorTheme = carouselSettings?.colorTheme;
    return (
        <div className={Styles.navArrowContainer}>
            <Icon
                size={Tokens.typescale5}
                onClick={clickHandler}
                name={
                    props.direction === 'Previous' ? 'keyboard_arrow_left' : 'keyboard_arrow_right'
                }
                className={classNames(
                    props.direction === 'Previous' ? Styles.prevArrow : Styles.nextArrow,
                    colorTheme === Theme.Dark ? Styles.navArrowDark : Styles.navArrowLight
                )}
            />
        </div>
    );
});

const NavButtonPrev = (props: CustomArrowProps) => (
    <CarouselNavArrow direction="Previous" {...props} />
);

const NavButtonNext = (props: CustomArrowProps) => <CarouselNavArrow direction="Next" {...props} />;

const urlParams = new URLSearchParams(window.location.search);
const stUri = urlParams.get('st_uri');
const redirectUri = urlParams.get('redirect_uri');
const baseUrl = !stUri
    ? !redirectUri
        ? 'https://go.servicetitan.com'
        : new URL(redirectUri).origin
    : new URL(stUri).origin;

export const LoginCarousel: FC<Props> = provide({
    singletons: [
        { provide: CONTENTFUL_API_TOKEN, useValue: new ContentfulApi(baseUrl) },
        MarketingCarouselStore,
    ],
})(
    observer(({ visible }) => {
        const [{ initialize, carouselData, carouselSettings, isInitializing }, loginStore] =
            useDependencies(MarketingCarouselStore, LoginStore);
        const {
            values: { isPreview },
        } = useQueryParams<{ isPreview: string }>();

        useEffect(() => {
            initialize({
                codeId: LOGIN_CAROUSEL_CODE_ID,
                utmSource: 'login',
                fallbackData: marketingCarouselFallbackData,
                isPreview: isPreview === 'true',
            });
        }, [initialize, isPreview]);

        if (!carouselData || isInitializing || loginStore.isInitializing) {
            return (
                <div className="w-100 h-100 d-f justify-content-center align-items-center">
                    <Spinner size="small" />
                </div>
            );
        }

        const isDarkTheme = carouselSettings?.colorTheme === Theme.Dark;
        const textColorClassName = isDarkTheme ? 'c-neutral-0' : 'c-neutral-300';

        const settings: Settings = {
            fade: true,
            dots: true,
            centerMode: true,
            draggable: false,
            speed: 750,
            autoplay: false,
            lazyLoad: 'progressive' as LazyLoadTypes,
            slidesToShow: 1,
            slidesToScroll: 1,
            className: classNames(Styles.slider, isDarkTheme ? Styles.dotsDark : Styles.dotsLight),
            prevArrow: <NavButtonPrev />,
            nextArrow: <NavButtonNext />,
        };

        return (
            <div className="h-100-i w-100-i">
                <Transition
                    transitionOnMount
                    duration={850}
                    animation="fade"
                    ease="ease"
                    visible={visible}
                    className="w-100-i"
                >
                    <div
                        className={Styles.carouselOuterContainer}
                        style={
                            carouselSettings?.backgroundImageUrl
                                ? {
                                      backgroundImage: `url(${carouselSettings.backgroundImageUrl})`,
                                  }
                                : undefined
                        }
                    >
                        <div className={Styles.carouselInnerContainer}>
                            <div className={Styles.carouselContainer}>
                                <Slider {...settings}>
                                    {carouselData.map(slideItem => (
                                        <Stack
                                            direction="column"
                                            justifyContent="center"
                                            alignItems="center"
                                            spacing={2}
                                            key={slideItem.headline}
                                        >
                                            <Transition
                                                transitionOnMount
                                                animation="scale"
                                                ease="ease out"
                                                duration={800}
                                            >
                                                <div
                                                    className={Styles.imageContainer}
                                                    style={{
                                                        backgroundImage: `url(${slideItem.imageUrl})`,
                                                    }}
                                                />
                                            </Transition>
                                            <Headline
                                                el="h2"
                                                size="large"
                                                className={classNames(
                                                    textColorClassName,
                                                    'm-b-2 m-t-0-i p-t-4'
                                                )}
                                            >
                                                {slideItem.headline}
                                            </Headline>
                                            <div className={Styles.descriptionContainer}>
                                                <BodyText className={textColorClassName}>
                                                    {slideItem.description}
                                                </BodyText>
                                            </div>
                                            <CarouselItemLinkButton
                                                href={slideItem.ctaLink}
                                                labelText={slideItem.ctaLabel}
                                            />
                                        </Stack>
                                    ))}
                                </Slider>
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>
        );
    })
);

interface ButtonProps {
    href: string;
    labelText?: string;
}

const CarouselItemLinkButton = observer((props: ButtonProps) => {
    const [{ carouselSettings }] = useDependencies(MarketingCarouselStore);
    const isDarkTheme = carouselSettings?.colorTheme === Theme.Dark;
    const buttonElement = (
        <Button
            primary={!isDarkTheme}
            fill={isDarkTheme ? undefined : 'subtle'}
            color={isDarkTheme ? 'grey' : undefined}
            size="small"
            rel="noopener noreferrer"
            target="_blank"
            href={!isDarkTheme ? props.href : undefined}
            className="m-t-3"
        >
            {props.labelText || 'Learn More'}
        </Button>
    );
    return isDarkTheme ? (
        <Link rel="noopener noreferrer" target="_blank" href={props.href}>
            {buttonElement}
        </Link>
    ) : (
        buttonElement
    );
});
